import config from 'Cloud/config';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { IS_ONPREMISE } from 'reactApp/appHelpers/configHelpers';
import { SHARING_FOLDERS_REDESIGN_BIZ, SHARING_REDESIGN_BIZ } from 'reactApp/appHelpers/featuresHelpers/features/sharingRedesignBiz';
import { isTabFolderSharingDesign } from 'reactApp/appHelpers/featuresHelpers/features/tabsFolderSharingDesign';
import { SharingContentBiz } from 'reactApp/components/SharingNewBiz/SharingContentBiz';
import { SHARING_VARIANT } from 'reactApp/components/SharingWindow/blocks/Weblink/WeblinkList/SharingNewWeblinkList.types';
import { SharingContentDefault } from 'reactApp/components/SharingWindow/content/SharingContentDefault';
import { SharingContentFiles } from 'reactApp/components/SharingWindow/content/SharingContentFiles/SharingContentFiles';
import { SharingContentFolders } from 'reactApp/components/SharingWindow/content/SharingContentFolders/SharingContentFolders';
import { SharingContentFoldersBiz } from 'reactApp/components/SharingWindow/content/SharingContentFoldersBiz/SharingContentFoldersBiz';
import type { SharingContentProps } from 'reactApp/components/SharingWindow/Sharing.types';
import { isAlbum } from 'reactApp/modules/albums/albums.types';
import { isOwnPublic as isOwnPublicSelector } from 'reactApp/modules/public/public.selectors';
import { getItemById, getStorageItemById } from 'reactApp/modules/storage/storage.selectors';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import type { RootState } from 'reactApp/store';

type UseComponentSharingArgs = Pick<SharingContentProps, 'itemStorage' | 'id'> & { isPhone?: boolean };

const SHARING_CONTENT = {
    [SHARING_VARIANT.folders]: {
        SharingContentComponent: SharingContentFolders,
        sharingVariant: SHARING_VARIANT.folders,
    },
    [SHARING_VARIANT.foldersBiz]: {
        SharingContentComponent: !SHARING_FOLDERS_REDESIGN_BIZ ? SharingContentBiz : SharingContentFoldersBiz,
        sharingVariant: SHARING_VARIANT.foldersBiz,
    },
    [SHARING_VARIANT.files]: {
        SharingContentComponent: SharingContentFiles,
        sharingVariant: SHARING_VARIANT.files,
    },
    [SHARING_VARIANT.filesBiz]: {
        SharingContentComponent: !SHARING_REDESIGN_BIZ ? SharingContentBiz : SharingContentFiles,
        sharingVariant: SHARING_VARIANT.filesBiz,
    },
    [SHARING_VARIANT.default]: {
        SharingContentComponent: SharingContentDefault,
        sharingVariant: SHARING_VARIANT.default,
    },
    [SHARING_VARIANT.albumsBiz]: {
        SharingContentComponent: SharingContentBiz,
        sharingVariant: SHARING_VARIANT.default,
    },
};

export const useComponentSharing = (args: UseComponentSharingArgs) => {
    const { itemStorage, id, isPhone } = args;

    const isPublic = !!config.get('PUBLIC');

    const item = useSelector((state: RootState) => {
        return itemStorage ? getStorageItemById(state, itemStorage, id) : getItemById(state, id);
    });

    const isBiz = useSelector(UserSelectors.isBizUser);
    const isOwnPublic = useSelector((state: RootState) => isPublic && isOwnPublicSelector(state));
    const isAlbumItem = isAlbum(item);
    const isFolder = item?.isFolder;

    const { SharingContentComponent, sharingVariant } = useMemo(() => {
        if (!item || (isPhone && !IS_ONPREMISE)) {
            return SHARING_CONTENT[SHARING_VARIANT.default];
        } else if (isAlbumItem) {
            if (IS_ONPREMISE || isBiz) {
                return SHARING_CONTENT[SHARING_VARIANT.albumsBiz];
            }
            return SHARING_CONTENT[SHARING_VARIANT.default];
        } else if (isFolder) {
            if (IS_ONPREMISE || isBiz) {
                return SHARING_CONTENT[SHARING_VARIANT.foldersBiz];
            } else if (isTabFolderSharingDesign) {
                return SHARING_CONTENT[SHARING_VARIANT.folders];
            }
        } else if (!isFolder && (!isPublic || isOwnPublic)) {
            if (IS_ONPREMISE || isBiz) {
                return SHARING_CONTENT[SHARING_VARIANT.filesBiz];
            }
            return SHARING_CONTENT[SHARING_VARIANT.files];
        }

        return SHARING_CONTENT[SHARING_VARIANT.default];
    }, [isAlbumItem, isBiz, isFolder, isPhone, isOwnPublic, isPublic, item]);

    return { SharingContentComponent, sharingVariant };
};
